/**
 * The AI answering form: whom the AI answers, how it sounds, how a call runs -
 * and the draft those fields edit.
 *
 * The draft keeps every box as the text the operator typed, and `toAgentPatch`
 * turns it into what the server will accept only when saving. Converting on
 * every keystroke would rewrite a half-typed number under the cursor.
 */
import { useT } from "../../../../../i18n/I18nProvider";
import {
  AGENT_GREETING_MAX,
  AGENT_MAX_CALL_SEC,
  AGENT_MAX_TEST_CALLERS,
  AGENT_MODEL_MAX,
  AGENT_MODEL_SUGGESTIONS,
  AGENT_SILENCE_MS,
  AI_AGENT_HANDOFF,
  AI_AGENT_MODES,
  type AiAgentHandoff,
  type AiAgentMode,
  type TelephonyAiAgent,
  type TelephonyAiAgentPatch,
} from "../../../../../lib/telephonyAgent";
import { CheckField, PickerField } from "../../../../ui";
import { Field, FieldBox, FieldGroup, Note, Pill, SelectPill } from "../../panel/fields";
import { useTtsVoices } from "../useTtsVoices";
import { agentVoiceOptions, readBoundedInt, readTestCallers, voiceIdHint } from "./aiAgentNotes";

export interface AgentDraft {
  enabled: boolean;
  modes: AiAgentMode[];
  /** One number per line, as typed. */
  testCallers: string;
  voice: string;
  model: string;
  greeting: string;
  silenceMs: string;
  maxCallSec: string;
  handoff: AiAgentHandoff;
}

export function toAgentDraft(a: TelephonyAiAgent): AgentDraft {
  return {
    enabled: a.enabled,
    modes: [...a.modes],
    testCallers: a.testCallers.join("\n"),
    voice: a.voice,
    model: a.model,
    greeting: a.greeting,
    silenceMs: String(a.silenceMs),
    maxCallSec: String(a.maxCallSec),
    handoff: a.handoff,
  };
}

/**
 * The draft as a PUT the schema cannot refuse. Every field is sent: this form
 * shows the whole group, so what is on screen is what gets stored.
 */
export function toAgentPatch(d: AgentDraft): TelephonyAiAgentPatch {
  return {
    enabled: d.enabled,
    modes: AI_AGENT_MODES.filter((m) => d.modes.includes(m)),
    testCallers: readTestCallers(d.testCallers).numbers,
    voice: d.voice.trim(),
    model: d.model.trim().slice(0, AGENT_MODEL_MAX),
    greeting: d.greeting.trim().slice(0, AGENT_GREETING_MAX),
    silenceMs: readBoundedInt(d.silenceMs, AGENT_SILENCE_MS),
    maxCallSec: readBoundedInt(d.maxCallSec, AGENT_MAX_CALL_SEC),
    handoff: d.handoff,
  };
}

export function AiAgentFields({ draft, onChange }: { draft: AgentDraft; onChange: (next: AgentDraft) => void }) {
  const t = useT();
  const { listing, loading } = useTtsVoices();

  function set<K extends keyof AgentDraft>(key: K, value: AgentDraft[K]) {
    onChange({ ...draft, [key]: value });
  }

  function toggleMode(mode: AiAgentMode, on: boolean) {
    const rest = draft.modes.filter((m) => m !== mode);
    set("modes", on ? [...rest, mode] : rest);
  }

  function pickHandoff(next: string) {
    const handoff = AI_AGENT_HANDOFF.find((h) => h === next);
    if (handoff) set("handoff", handoff);
  }

  const callers = readTestCallers(draft.testCallers);
  const voice = agentVoiceOptions(draft.voice, listing?.voices ?? []);
  // Nothing to choose from once the list has answered: no key, or a workspace
  // with no voices. The saved row does not count - it is not a choice.
  const noVoices = !loading && voice.options.every((o) => o.saved);

  return (
    <>
      <FieldGroup title={t("settings.aiAgent.whoGroup")}>
        <Field
          label={t("settings.aiAgent.testCallers")}
          hint={t("settings.aiAgent.testCallersHint", { max: AGENT_MAX_TEST_CALLERS })}
        >
          {/* `crm-area`, not a textarea inside `crm-inp`: that pill is a fixed
              flex row whose child rules only reach input and select. */}
          <textarea
            className="crm-area min-h-[86px] font-mono text-[12.5px]"
            rows={3}
            value={draft.testCallers}
            placeholder="+37255512345"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => set("testCallers", e.target.value)}
          />
        </Field>
        {/* Said before saving, not discovered after it: the server drops a
            line it cannot read without a word, and the owner would then ring
            from a number the AI was never told about. */}
        {callers.invalid.length > 0 ? (
          <Note tone="warn">{t("settings.aiAgent.testCallersInvalid", { lines: callers.invalid.join(", ") })}</Note>
        ) : null}
        {callers.overflow.length > 0 ? (
          <Note tone="warn">
            {t("settings.aiAgent.testCallersOverflow", { max: AGENT_MAX_TEST_CALLERS, lines: callers.overflow.join(", ") })}
          </Note>
        ) : null}

        <FieldBox label={t("settings.aiAgent.modesLabel")} hint={t("settings.aiAgent.modesHint")}>
          <div className="grid gap-2 sm:grid-cols-2">
            {AI_AGENT_MODES.map((mode) => (
              <CheckField
                key={mode}
                checked={draft.modes.includes(mode)}
                onChange={(on) => toggleMode(mode, on)}
                label={t(`settings.aiAgent.mode.${mode}`)}
              />
            ))}
          </div>
        </FieldBox>
        {/* Editable rather than disabled: the owner can set the modes up now
            and clear the test numbers later, and this line says why ticking
            them changes nothing today. */}
        {callers.numbers.length > 0 ? <Note tone="mut">{t("settings.aiAgent.modesIgnored")}</Note> : null}
      </FieldGroup>

      <FieldGroup title={t("settings.aiAgent.speechGroup")}>
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          {/* `FieldBox`, not `Field`: both menus are buttons, and a label would
              forward its own clicks to them. */}
          <FieldBox label={t("settings.aiAgent.voice")} hint={t("settings.aiAgent.voiceHint")}>
            <PickerField
              value={voice.selected}
              onChange={(next) => set("voice", next)}
              options={voice.options.map((o) => ({
                value: o.value,
                label: o.saved ? t("settings.aiAgent.voiceSaved", { voice: o.label }) : o.label,
                hint: voiceIdHint(o),
              }))}
              label={t("settings.aiAgent.voice")}
              placeholder={loading ? t("settings.aiAgent.voiceLoading") : t("settings.aiAgent.voiceNone")}
              searchPlaceholder={t("settings.aiAgent.voiceSearch")}
              emptyLabel={t("settings.aiAgent.voiceNoMatch")}
            />
          </FieldBox>
          {/* `allowCustom`, because this build's list of model ids goes stale
              the week Google renames one and a settings page that refuses an id
              it has not heard of needs a release to stay usable. */}
          <FieldBox label={t("settings.aiAgent.model")} hint={t("settings.aiAgent.modelHint")}>
            <PickerField
              mono
              allowCustom
              value={draft.model}
              onChange={(next) => set("model", next)}
              options={AGENT_MODEL_SUGGESTIONS.map((m) => ({ value: m, label: m }))}
              label={t("settings.aiAgent.model")}
              placeholder={AGENT_MODEL_SUGGESTIONS[0]}
              searchPlaceholder={t("settings.aiAgent.modelSearch")}
              customLabel={t("settings.aiAgent.modelCustom")}
              emptyLabel={t("settings.aiAgent.modelNoMatch")}
            />
          </FieldBox>
        </div>
        {noVoices ? <Note tone="mut">{t("settings.aiAgent.voicesEmpty")}</Note> : null}
        <Field
          label={t("settings.aiAgent.greeting")}
          hint={`${t("settings.aiAgent.greetingHint")} ${t("settings.aiAgent.greetingCount", { count: draft.greeting.length, max: AGENT_GREETING_MAX })}`}
        >
          <textarea
            className="crm-area min-h-[86px]"
            rows={3}
            maxLength={AGENT_GREETING_MAX}
            value={draft.greeting}
            onChange={(e) => set("greeting", e.target.value)}
          />
        </Field>
      </FieldGroup>

      <FieldGroup title={t("settings.aiAgent.callGroup")}>
        {/* Both timings hold three or four digits, so both wear a pill the
            width of the answer, as the co-pilot's do. */}
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field
            label={t("settings.aiAgent.silenceMs")}
            hint={t("settings.aiAgent.silenceMsHint", {
              min: AGENT_SILENCE_MS.min,
              max: AGENT_SILENCE_MS.max,
              fallback: AGENT_SILENCE_MS.fallback,
            })}
          >
            <Pill
              className="h-10 max-w-[120px]"
              value={draft.silenceMs}
              onChange={(next) => set("silenceMs", next)}
              inputMode="numeric"
              placeholder={String(AGENT_SILENCE_MS.fallback)}
            />
          </Field>
          <Field
            label={t("settings.aiAgent.maxCallSec")}
            hint={t("settings.aiAgent.maxCallSecHint", {
              min: AGENT_MAX_CALL_SEC.min,
              max: AGENT_MAX_CALL_SEC.max,
              fallback: AGENT_MAX_CALL_SEC.fallback,
            })}
          >
            <Pill
              className="h-10 max-w-[120px]"
              value={draft.maxCallSec}
              onChange={(next) => set("maxCallSec", next)}
              inputMode="numeric"
              placeholder={String(AGENT_MAX_CALL_SEC.fallback)}
            />
          </Field>
          <FieldBox label={t("settings.aiAgent.handoffLabel")} hint={t("settings.aiAgent.handoffHint")}>
            <SelectPill value={draft.handoff} onChange={pickHandoff} label={t("settings.aiAgent.handoffLabel")}>
              {AI_AGENT_HANDOFF.map((to) => (
                <option key={to} value={to}>
                  {t(`settings.aiAgent.handoffTo.${to}`)}
                </option>
              ))}
            </SelectPill>
          </FieldBox>
        </div>
      </FieldGroup>
    </>
  );
}
